import React, { useEffect, useState } from 'react';
import { useCall } from './CallProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Phone, PhoneOff, Video, Mic } from 'lucide-react';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';

export function IncomingCallNotification() {
  const { incomingCall, acceptCall, rejectCall } = useCall();
  const [isVisible, setIsVisible] = useState(false);
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (incomingCall) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [incomingCall]);

  // Only show for incoming calls
  if (!isVisible || !incomingCall) {
    return null;
  }
  return (
    <div className={cn('fixed z-[100] animate-in', isMobile ? 'left-3 right-3 bottom-[max(env(safe-area-inset-bottom),0.75rem)] slide-in-from-bottom-2' : 'top-4 right-4 slide-in-from-top-2')}>
      <Card className={cn('bg-white shadow-2xl border-0 overflow-hidden', isMobile ? 'w-full' : 'max-w-sm')}>
        <CardContent className="p-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-4">
            <div className="flex items-center gap-2 mb-2">
              {incomingCall.type === 'video' ? (
                <Video className="w-5 h-5" />
              ) : (
                <Phone className="w-5 h-5" />
              )}
              <span className="font-medium">
                Incoming {incomingCall.type === 'video' ? 'Video' : 'Voice'} Call
              </span>
            </div>
          </div>

          {/* Caller info */}
          <div className={cn('text-center', isMobile ? 'p-4' : 'p-6')}>
            <div className="relative mb-4">
              <Avatar className={cn('mx-auto', isMobile ? 'w-16 h-16' : 'w-20 h-20')}>
                <AvatarImage src={incomingCall.caller.avatar} />
                <AvatarFallback className="text-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                  {incomingCall.caller.name[0]}
                </AvatarFallback>
              </Avatar>
              
              {/* Pulsing animation */}
              <div className="absolute inset-0 rounded-full border-4 border-blue-400 animate-ping opacity-30"></div>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {incomingCall.caller.name}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {incomingCall.type === 'video' ? 'wants to video chat' : 'is calling you'}
            </p>

            {/* Action buttons */}
            <div className={cn('flex items-center justify-center', isMobile ? 'gap-3' : 'gap-4')}>
              <Button
                size="lg"
                variant="destructive"
                onClick={() => rejectCall(incomingCall.id)}
                className={cn('rounded-full bg-red-500 hover:bg-red-600', isMobile ? 'w-12 h-12' : 'w-14 h-14')}
              >
                <PhoneOff className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} />
              </Button>
              
              <Button
                size="lg"
                onClick={() => acceptCall(incomingCall.id)}
                className={cn('rounded-full bg-green-500 hover:bg-green-600', isMobile ? 'w-12 h-12' : 'w-14 h-14')}
              >
                <Phone className={cn(isMobile ? 'w-5 h-5' : 'w-6 h-6')} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
